import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface InviteLinkData {
  code: string | null
  type: 'caregiver' | 'assisted' | null
  isProcessing: boolean
  error: string | null
  isFromUrl: boolean
}

export const useInviteLink = () => {
  // Verificación de seguridad para el contexto del Router
  let location, navigate
  try {
    location = useLocation()
    navigate = useNavigate()
  } catch (error) {
    console.warn('⚠️ Router context not available, using fallback values')
    location = { pathname: '/', search: '' }
    navigate = () => {}
  }

  const [inviteData, setInviteData] = useState<InviteLinkData>({
    code: null,
    type: null,
    isProcessing: false,
    error: null,
    isFromUrl: false
  })

  useEffect(() => {
    // Solo procesar si tenemos un location válido
    if (!location || typeof location.pathname !== 'string') {
      return
    }

    // Verificar si estamos en una ruta de invitación
    const pathMatch = location.pathname.match(/^\/invite\/([A-Z0-9-]+)$/i)
    
    // O si hay parámetros de query con códigos de invitación
    const urlParams = new URLSearchParams(location.search || '')
    const queryCode = urlParams.get('invite') || urlParams.get('code')
    const inviteType = urlParams.get('type') as 'caregiver' | 'assisted' | null
    
    const inviteCode = pathMatch?.[1] || queryCode
    
    if (inviteCode && !inviteData.code) {
      console.log('🔄 Detected invite code:', inviteCode, 'type:', inviteType)
      setInviteData({
        code: inviteCode.toUpperCase(),
        type: inviteType || 'caregiver', // Default to caregiver if no type specified
        isProcessing: false,
        error: null,
        isFromUrl: true
      })

      // Limpiar la URL para que no quede el código visible (solo si navigate está disponible)
      if ((pathMatch || queryCode) && navigate && typeof navigate === 'function') {
        try {
          navigate('/', { replace: true })
        } catch (error) {
          console.warn('⚠️ Could not navigate:', error)
        }
      }
    }
  }, [location, navigate, inviteData.code])

  const processInvite = async (processor: (code: string) => Promise<void>) => {
    if (!inviteData.code) return

    setInviteData(prev => ({ ...prev, isProcessing: true, error: null }))

    try {
      await processor(inviteData.code)
      setInviteData(prev => ({ ...prev, isProcessing: false, code: null }))
    } catch (error) {
      console.error('Error processing invite:', error)
      setInviteData(prev => ({ 
        ...prev, 
        isProcessing: false, 
        error: error instanceof Error ? error.message : 'Error al procesar la invitación'
      }))
    }
  }

  const clearInvite = () => {
    setInviteData({
      code: null,
      type: null,
      isProcessing: false,
      error: null,
      isFromUrl: false
    })
  }

  return {
    inviteCode: inviteData.code,
    inviteType: inviteData.type,
    isProcessing: inviteData.isProcessing,
    error: inviteData.error,
    isFromUrl: inviteData.isFromUrl,
    processInvite,
    clearInvite
  }
} 